// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Reason = any;

type Subscriber<T> = {
    resolve: (value: T) => void;
    reject: (reason: Reason) => void;
};

export class Lazy<T> {
    private _value: T | undefined;
    private _waiting: Subscriber<T>[] = [];
    private _error: Reason | undefined;

    constructor(trigger?: Promise<T>) {
        if (!trigger) return;

        (async () => {
            try {
                this.setValue(await trigger);
            } catch (reason) {
                this.setError(reason);
            }
        })();
    }

    get(): Promise<T> {
        if (this._error) {
            return Promise.reject(this._error);
        }

        if (this._value) {
            return Promise.resolve(this._value);
        }

        return new Promise((resolve, reject) => {
            this._waiting.push({
                resolve: value => resolve(value),
                reject: reason => reject(reason),
            });
        });
    }

    setValue(value: T): void {
        this._value = value;
        this._waiting.splice(0).forEach(w => w.resolve(value));
    }

    setError(reason: unknown): void {
        this._error = reason;
        this._waiting.splice(0).forEach(w => {
            try {
                w.reject(reason);
            } catch (e) {
                console.warn(e);
            }
        });
    }

    get currentValue() {
        return this._value;
    }

    clear(): void {
        this._value = undefined;
    }
}

export function lazy<T>(trigger?: Promise<T>) {
    return new Lazy<T>(trigger);
}
